import BaseApiService from './BaseApiService'
import type { ApprovalLevel, OverridesBelow, TeamMember } from '../types'

export interface TeamPromotionApproval {
  setting: ApprovalLevel | null
  level: ApprovalLevel
  required: boolean
  overridesBelow: OverridesBelow
  /** False when the team has fewer than two active editors or admins; 'dual' is then refused. */
  dualAvailable: boolean
  dualUnavailableReason: string | null
}

/** What deactivating a member does to dual control (settings/deactivate-member/impact). */
export interface DeactivateImpact {
  activeAfter: number
  dualControl: { team: boolean; folders: number; templates: number; pendingRequests: number }
  /** Dual control is in use and would be left unsatisfiable: deactivate needs dualControlFallback. */
  fallbackRequired: boolean
}

/** What changing a member's role does to dual control (settings/update-role/impact). */
export type RoleChangeImpact = DeactivateImpact

export type DualControlFallback = 'auto' | 'self'


class SettingsApiService extends BaseApiService {
  invite(email: string, securityRole?: number): Promise<{ userId: string; email: string }> {
    return this.post('/v1/settings/invite', { email, securityRole })
  }

  listTeamUsers(): Promise<TeamMember[]> {
    return this.get('/v1/settings/team/users')
  }

  transferAdmin(targetUserId: string): Promise<{ transferred: boolean }> {
    return this.post('/v1/settings/transfer-admin', { targetUserId })
  }

  updateRoleImpact(targetUserId: string, newRole: number): Promise<RoleChangeImpact> {
    return this.post('/v1/settings/update-role/impact', { targetUserId, newRole })
  }

  /** 409 DUAL_CONTROL_FALLBACK_REQUIRED when the impact says a fallback is needed and none is sent. */
  updateRole(targetUserId: string, newRole: number, dualControlFallback?: DualControlFallback): Promise<{ updated: boolean }> {
    return this.post('/v1/settings/update-role', { targetUserId, newRole, dualControlFallback })
  }

  deactivateMemberImpact(targetUserId: string): Promise<DeactivateImpact> {
    return this.post('/v1/settings/deactivate-member/impact', { targetUserId })
  }

  /** 409 DUAL_CONTROL_FALLBACK_REQUIRED when the impact says a fallback is needed and none is sent. */
  deactivateMember(targetUserId: string, dualControlFallback?: DualControlFallback): Promise<{ success: boolean }> {
    return this.post('/v1/settings/deactivate-member', { targetUserId, dualControlFallback })
  }

  reactivateMember(targetUserId: string): Promise<{ success: boolean }> {
    return this.post('/v1/settings/reactivate-member', { targetUserId })
  }

  getTeamPersistence(): Promise<{ noStore: boolean }> {
    return this.post('/v1/settings/team-persistence/get')
  }

  setTeamPersistence(noStore: boolean): Promise<{ noStore: boolean }> {
    return this.post('/v1/settings/team-persistence', { noStore })
  }

  /** setting null: not set, so not required. required is the effective value. */
  getTeamPromotionApproval(): Promise<TeamPromotionApproval> {
    return this.post('/v1/settings/team-promotion-approval/get', {})
  }

  /** applyToDescendants clears every folder and template override in the team. */
  setTeamPromotionApproval(level: ApprovalLevel | null, applyToDescendants: boolean): Promise<{ setting: ApprovalLevel | null; level: ApprovalLevel; required: boolean; cleared: OverridesBelow }> {
    return this.post('/v1/settings/team-promotion-approval', { level, applyToDescendants })
  }

  updateTeam(name: string): Promise<{ success: boolean }> {
    return this.post('/v1/settings/update-team', { name })
  }
}

export const settingsApiService = new SettingsApiService()
